import './index.scss';
import React from 'react';
import moment from 'moment';
import { toast } from 'react-semantic-toasts';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Table,
  Icon,
  Message,
  Modal,
  Header,
  Button,
  Label,
  Popup,
} from 'semantic-ui/semantic-ui-imports';
import {
  getProjects,
  PaginatedProjectsResponse,
  archiveProject,
  deleteProject,
} from '../../services/projects';
import { Project } from '../../Types/Project';
import IconButton from '../IconButton';

interface Props {
  projectType?: 'user_project' | 'quote_request';
}

const ProjectList = ({ projectType }: Props) => {
  const { t } = useTranslation();

  const navigate = useNavigate();
  const [projectCount, setprojectCount] = React.useState<number>();
  const [projects, setProjects] = React.useState<Array<Project>>([]);
  const [openArchiveConfirm, setOpenArchiveConfirm] = React.useState(false);
  const [openDeleteConfirm, setOpenDeleteConfirm] = React.useState(false);
  const [
    selectedProject,
    setSelectedProject,
  ] = React.useState<Project | null>();

  const getList = async () => {
    try {
      const res: PaginatedProjectsResponse | null = await getProjects(
        projectType ? { type: projectType } : undefined
      );
      if (res) {
        setProjects(res.results);
        setprojectCount(res.count);
      }
    } catch {
      toast({
        type: 'error',
        title: t('resultList.deleteError'),
        time: 4000,
        size: 'small',
      });
    }
  };

  React.useEffect(() => {
    getList();
  }, []);

  const handleArchiveProject = async (id: string | number) => {
    try {
      await archiveProject(id);
      const idx = projects.findIndex((p) => p.id === id);
      projects.splice(idx, 1);
      setProjects([...projects]);
    } catch {
      toast({
        type: 'error',
        title: t('projectList.archiveError'),
        time: 4000,
        size: 'small',
      });
    }
  };

  const removeProject = async (id: string | number) => {
    try {
      await deleteProject(id);
      const idx = projects.findIndex((p) => p.id === id);
      projects.splice(idx, 1);
      setProjects([...projects]);
    } catch {
      toast({
        type: 'error',
        title: t('projectList.deleteError'),
        time: 4000,
        size: 'small',
      });
    }
  };

  // Check if project can be deleted (no results have been through quote flow)
  const canDeleteProject = (project: Project): boolean =>
    !project.has_quote_requested;

  const goToProject = (id: string | number) => {
    navigate(`/projects/${id}`);
  };

  return (
    <div className="project-list">
      {projects.length === 0 && (
        <>
          <Message info compact className="project-list__no-projects">
            <Message.Header>{t('projectList.noProjects')}</Message.Header>
          </Message>
        </>
      )}
      <Header as="h3">
        {t('projectList.projects')}({projectCount})
      </Header>
      <Table celled selectable>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell>
              <h4>{t('projectList.projectName')}</h4>
            </Table.HeaderCell>
            <Table.HeaderCell>
              <h4>{t('projectList.projectCreationDate')}</h4>
            </Table.HeaderCell>
            <Table.HeaderCell>
              <h4>{t('projectList.actionsHeader')}</h4>
            </Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {projects.map((data: Project) => (
            <Table.Row key={data.id} onClick={() => goToProject(data.id)}>
              <Table.Cell width={10}>
                {data.name}
                {data.has_quote_requested && (
                  <Label
                    color="orange"
                    size="tiny"
                    style={{ marginLeft: '0.5em' }}
                  >
                    {t('projectList.quoteRequested')}
                  </Label>
                )}
                {data.has_pending_quotes && (
                  <Label
                    color="yellow"
                    size="tiny"
                    style={{ marginLeft: '0.5em' }}
                  >
                    {t('projectList.pendingQuotes')}
                  </Label>
                )}
              </Table.Cell>
              <Table.Cell width={4}>
                {moment(data.created_at).calendar()}
              </Table.Cell>
              <Table.Cell width={2} floated="right">
                <div className="project-list__icon">
                  {data.has_pending_quotes ? (
                    <Popup
                      content={t('projectList.cannotArchivePending')}
                      trigger={
                        <span>
                          <IconButton
                            icon="archive"
                            disabled
                            onClick={(ev: React.MouseEvent) => {
                              ev.preventDefault();
                              ev.stopPropagation();
                            }}
                          />
                        </span>
                      }
                    />
                  ) : (
                    <Popup
                      content={t('projectList.archiveTooltip')}
                      trigger={
                        <span>
                          <IconButton
                            icon="archive"
                            onClick={(ev: React.MouseEvent) => {
                              ev.preventDefault();
                              ev.stopPropagation();
                              setSelectedProject(data);
                              setOpenArchiveConfirm(true);
                            }}
                          />
                        </span>
                      }
                    />
                  )}
                  {canDeleteProject(data) ? (
                    <Popup
                      content={t('projectList.deleteTooltip')}
                      trigger={
                        <span>
                          <IconButton
                            icon="trash"
                            onClick={(ev: React.MouseEvent) => {
                              ev.preventDefault();
                              ev.stopPropagation();
                              setSelectedProject(data);
                              setOpenDeleteConfirm(true);
                            }}
                          />
                        </span>
                      }
                    />
                  ) : (
                    <Popup
                      content={t('projectList.cannotDeleteQuoted')}
                      trigger={
                        <span>
                          <IconButton
                            icon="trash"
                            disabled
                            onClick={(ev: React.MouseEvent) => {
                              ev.preventDefault();
                              ev.stopPropagation();
                            }}
                          />
                        </span>
                      }
                    />
                  )}
                </div>
              </Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table>
      <Modal
        onClose={() => setOpenArchiveConfirm(false)}
        open={openArchiveConfirm}
        size="small"
      >
        <Header icon>
          <Icon name="archive" color="orange" />
          {t('projectList.archiveProject')}?
        </Header>
        <Modal.Content>
          <p>{`${t('projectList.archiveModalText')} "${
            selectedProject?.name
          }"`}</p>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={() => setOpenArchiveConfirm(false)}>
            <Icon name="cancel" /> {t('projectList.cancel')}
          </Button>
          <Button
            color="orange"
            onClick={() => {
              if (selectedProject) {
                handleArchiveProject(selectedProject.id);
                setOpenArchiveConfirm(false);
                setSelectedProject(null);
              }
            }}
          >
            <Icon name="checkmark" /> {t('projectList.ok')}
          </Button>
        </Modal.Actions>
      </Modal>
      <Modal
        onClose={() => setOpenDeleteConfirm(false)}
        open={openDeleteConfirm}
        size="small"
      >
        <Header icon>
          <Icon name="warning sign" color="red" />
          {t('projectList.deleteProject')}?
        </Header>
        <Modal.Content>
          <p>{`${t('projectList.deleteModalText')} "${
            selectedProject?.name
          }"`}</p>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={() => setOpenDeleteConfirm(false)}>
            <Icon name="cancel" /> {t('projectList.cancel')}
          </Button>
          <Button
            color="red"
            onClick={() => {
              if (selectedProject) {
                removeProject(selectedProject.id);
                setOpenDeleteConfirm(false);
                setSelectedProject(null);
              }
            }}
          >
            <Icon name="checkmark" /> {t('projectList.ok')}
          </Button>
        </Modal.Actions>
      </Modal>
    </div>
  );
};

export default ProjectList;
